/**
 * Manual (attested) checks -> fleet report rows.
 *
 * Automated checks are FOUND: a producer wrote a dated fact and _rows.py unpacks it.
 * Manual checks are GENERATED: there is no fact, no producer and no daily trail —
 * just one accountprofile note per (account, check), overwritten on each edit. So
 * this walks accounts x selected checks and emits a row either way, because for a
 * compliance sweep the account with NOTHING on file is the finding.
 *
 * Decisions this encodes:
 *  - Date/text values are NOT judged. statusFromManual scores any recorded date as
 *    a pass, which is fine on Technical Health (a vCIO reads the date in context)
 *    but would make a fleet-wide "who is overdue" sweep come back all-green. Those
 *    rows report RECORDED and carry the date; the reader judges.
 *  - Booleans ARE scored, via statusFromManual, so truthiness parsing has one home.
 *  - No plan/tier filtering: a check the customer may not need still gets a row.
 *  - One row per account per check, NEVER per day. A manual value has no daily
 *    grain, so a date range must not multiply these rows.
 *
 * Row shape mirrors cma-api routes/cma/audit/_rows.py exactly, so manual and
 * automated rows share one table with no special-casing downstream.
 */
import { statusFromManual } from '../Customer/health/healthRegistry';

export const MANUAL_RUN_ID = 'MANUAL';

const keyFor = (account, checkKey) => `${account}::${checkKey}`;

/** {account::check_key -> profile row}, manual directives only. */
export function indexManualDirectives(profileRows) {
  const index = {};
  for (const row of profileRows || []) {
    const d = row?.directive;
    if (!d || d.kind !== 'manual' || !d.check_key || !row.account) continue;
    index[keyFor(row.account, d.check_key)] = row;
  }
  return index;
}

export function buildManualRows({
  manualKeys,
  accounts,
  directives,
  asOfDate,
  labelByKey = {},
}) {
  const index = indexManualDirectives(directives);
  const rows = [];

  for (const account of accounts) {
    for (const checkKey of manualKeys) {
      const row = index[keyFor(account, checkKey)];
      const d = row?.directive;

      let result;
      let value = null;

      if (!d) {
        result = 'NOT RECORDED';
      } else if (d.value_type === 'boolean' || typeof d.value === 'boolean') {
        const scored = statusFromManual({ value: d.value, value_type: d.value_type });
        result = scored.status === 'pass' ? 'PASS' : 'FAIL';
        value = scored.value;
      } else {
        result = 'RECORDED';
        value = d.value == null ? '' : String(d.value);
      }

      rows.push({
        account,
        auditDate: asOfDate,
        auditRunId: MANUAL_RUN_ID,
        auditType: MANUAL_RUN_ID,
        checkKey,
        target: account,
        targetType: 'account',
        result,
        message: row?.content || null,
        value,
        observedAt: row?.lastUpdated || null,
        overridden: false,
        extra: {
          sourceKind: 'manual',
          label: labelByKey[checkKey] || checkKey,
          valueType: d?.value_type || null,
          recordedBy: (row?.last_updated_by || '').replace('user:', '') || null,
        },
      });
    }
  }

  return rows;
}
